import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Users, Clock, History } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getGradeColorClass = (grade) => {
  if (!grade) return "bg-gray-100 text-gray-500";
  if (grade >= 70) return "bg-green-100 text-green-700";
  if (grade >= 55) return "bg-blue-100 text-blue-700";
  if (grade >= 45) return "bg-yellow-100 text-yellow-700";
  return "bg-red-100 text-red-700";
};

const formatTool = (t) => t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

export function CoachEvalHistory({ athleteId, athleteName }) {
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [viewMode, setViewMode] = useState("history");
  const [expandedDates, setExpandedDates] = useState({});

  useEffect(() => {
    if (athleteId) fetchData();
  }, [athleteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const historyRes = await fetch(`${API_URL}/api/evaluations/history/${athleteId}`, {
        credentials: "include", headers
      });
      if (historyRes.ok) setHistoryData(await historyRes.json());

      const compareRes = await fetch(`${API_URL}/api/evaluations/compare/${athleteId}`, {
        credentials: "include", headers
      });
      if (compareRes.ok) setComparisonData(await compareRes.json());
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading evaluation history...</p>
      </div>
    );
  }

  const noEvals = !historyData?.total_evaluations;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-blue-600 to-blue-900 p-6 text-white">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <History size={24} />
          Evaluation History
        </h2>
        <p className="text-blue-100 mt-1">
          {athleteName} - {historyData?.total_evaluations || 0} evaluations from {historyData?.evaluators_count || 0} scouts
        </p>
      </div>

      <div className="p-6">
        {noEvals ? (
          <div className="text-center py-12 text-gray-500">
            <p>No evaluations found for this athlete.</p>
            <p className="text-sm mt-1">Evaluations will appear here once scouts submit them.</p>
          </div>
        ) : (
          <div>
            <div className="flex gap-2 mb-6">
              <Button variant={viewMode === "history" ? "default" : "outline"} size="sm" onClick={() => setViewMode("history")}>
                <Clock size={14} className="mr-1" /> Historical View
              </Button>
              <Button variant={viewMode === "comparison" ? "default" : "outline"} size="sm" onClick={() => setViewMode("comparison")}>
                <Users size={14} className="mr-1" /> Scout Comparison
              </Button>
            </div>

            {viewMode === "history" && (
              <div className="space-y-4">
                {Object.entries(historyData?.history_by_date || {}).sort((a, b) => b[0].localeCompare(a[0])).map(([date, evals]) => (
                  <div key={date} className="border rounded-lg">
                    <button
                      onClick={() => setExpandedDates(p => ({ ...p, [date]: !p[date] }))}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
                    >
                      <span className="font-semibold">{new Date(date).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</span>
                      <span className="text-gray-500">({evals.length} eval{evals.length > 1 ? "s" : ""})</span>
                    </button>
                    {expandedDates[date] && (
                      <div className="p-4 space-y-4">
                        {evals.map((ev, i) => (
                          <div key={i} className="border rounded-lg p-4">
                            <div className="flex justify-between items-center mb-4">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                  {ev.evaluator?.name?.charAt(0) || "?"}
                                </div>
                                <span className="font-medium">{ev.evaluator?.name || "Unknown Scout"}</span>
                              </div>
                              {ev.nsb_composite_score && (
                                <span className={`px-3 py-1 rounded-lg font-bold ${getGradeColorClass(ev.nsb_composite_score)}`}>
                                  {ev.nsb_composite_score} NSB
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                              {Object.entries(ev.tool_results || {}).map(([tool, data]) => (
                                <div key={tool} className={`p-2 rounded text-center text-sm ${getGradeColorClass(data?.grade)}`}>
                                  <div className="font-medium truncate">{formatTool(tool)}</div>
                                  <div className="text-lg font-bold">{data?.grade || "--"}</div>
                                </div>
                              ))}
                            </div>
                            {ev.written_notes && (
                              <div className="mt-3 p-3 bg-yellow-50 rounded text-sm italic">{ev.written_notes}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {viewMode === "comparison" && comparisonData?.comparison?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-3 border-b font-semibold">Scout</th>
                      {comparisonData.tools?.map(t => <th key={t} className="text-center p-3 border-b font-semibold">{formatTool(t)}</th>)}
                      <th className="text-center p-3 border-b font-semibold bg-blue-50">NSB Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.comparison.map((row, i) => (
                      <tr key={row.scout_id} className={i % 2 ? "bg-gray-50" : ""}>
                        <td className="p-3 border-b">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                              {row.scout_name?.charAt(0) || "?"}
                            </div>
                            {row.scout_name}
                          </div>
                        </td>
                        {comparisonData.tools?.map(t => (
                          <td key={t} className="text-center p-3 border-b">
                            {row.grades?.[t]?.grade ? (
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${getGradeColorClass(row.grades[t].grade)}`}>
                                {row.grades[t].grade}
                              </span>
                            ) : "--"}
                          </td>
                        ))}
                        <td className="text-center p-3 border-b bg-blue-50">
                          {row.overall_grade ? (
                            <span className={`px-3 py-1 rounded-lg font-bold ${getGradeColorClass(row.overall_grade)}`}>
                              {row.overall_grade}
                            </span>
                          ) : "--"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {viewMode === "comparison" && (!comparisonData?.comparison?.length) && (
              <div className="text-center py-12 text-gray-500">
                <Users className="mx-auto text-gray-300" size={48} />
                <p className="mt-4">Not enough evaluations to compare.</p>
                <p className="text-sm">At least 2 scouts need to evaluate this athlete.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
